import RestaurantIcon from '@material-ui/icons/Restaurant';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Checkbox, Button } from '@material-ui/core';
import classNames from 'classnames';
import BlockOutsService from 'external-services/blockouts-service/blockouts.service';
import color from 'design-v2/globals/colors/colors.module.css';
import style from './style.module.css';
import ScheduleIcon from '@material-ui/icons/Schedule';
import Switch from '@material-ui/core/Switch';
import ActionButton from 'design-v2/common/ActionButton';
import {
  IServiceItem,
  IBlockout,
  NBIProject,
  ITableItem, ILayout,
} from 'shared-types';
import { useSelector } from 'react-redux';
import { DiaryRootState } from 'appRoot/rootReducer';
import { APP_COLORS, Colors } from 'design-v2/globals/colors/colors';
import { useAppDispatch } from 'app/appRoot/Store';
import { scheduleActions, setSelectedSchedule } from 'app/reducers/schedule/slice';
import TablesLookupService from 'external-services/tables-lookup-service/tablesLookup.service';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import DateUtilsService from 'external-services/date-utils-service';

let timeOutCreateBlockOut: NodeJS.Timeout;
enum BlockoutServiceType {
  BLOCKOUT_SERVICE = 'BLOCKOUT_SERVICE',
  HIDE_SERVICE = 'HIDE_SERVICE',
}
export default function NewBlockout({ exit, saveCallback, createdBy }: { exit: () => void, saveCallback?: (msg: string) => void, createdBy?: NBIProject }) {
  const [blockoutForm, setBlockoutForm] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [blockAllServices, setBlockAllServices] = useState<Boolean>(false);
  const [hideAllServices, setHideAllServices] = useState(false);
  const [originalHidingServiceIds, setOriginalHidingServiceIds] = useState<string[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [blockouts, setBlockouts] = useState<IBlockout[]>([]);
  const [blockoutServiceType, setBlockoutServiceType] = useState<BlockoutServiceType>(BlockoutServiceType.BLOCKOUT_SERVICE);
  const layouts = useSelector((state: DiaryRootState) => state.layoutReducer.couchReducedLayouts);
  const isWideVersion = useSelector((state: DiaryRootState) => state.bookingCreatorSettingReducer.isWideVersion);
  const servicesStore = useSelector(
    (state: DiaryRootState) => state.serviceReducer.currentServiceViews
  );
  const selectedDate = useSelector((state: DiaryRootState) => state.scheduleReducer.selectedDate);
  const serviceListView = useMemo(
    () =>
      servicesStore
        .filter((item) => item.service)
        .map((item) => {
          return {
            ...item,
            service: item.service as IServiceItem,
          };
        }),
    []
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    const dateStr = DateUtilsService.dateToDateStr(selectedDate);
    BlockOutsService.getBlockoutsOnDate(selectedDate).then(setBlockouts);
    BlockOutsService.getHiddenServiceByDate(dateStr).then((dto) => {
      dispatch(scheduleActions.setHiddenServices(dto.serviceIds));
      setOriginalHidingServiceIds(dto.serviceIds);
    })
  }, [selectedDate]);

  const selectedTimeServices = useMemo(() => {
    return Object.keys(blockoutForm)
      .filter((id) => {
        const service = serviceListView.find(({ service }) => service._id === id)?.service;
        if (!service) {
          return false;
        }
        const { startTime, endTime } = service;
        const isExisted = blockouts.find(
          ({ startDateTime, endDateTime }) =>
            startDateTime.toString() === startTime.toString() &&
            endDateTime.toString() === endTime.toString()
        );

        return blockoutForm[id] && !isExisted;
      })
      .map((id) => {
        const service = serviceListView.find(({ service }) => service._id === id).service;
        const layoutIds = service.sectionLayouts.map((layout) => layout.layoutId).filter(Boolean);
        return {
          startDateTime: service.startTime,
          endDateTime: service.endTime,
          name: service.name,
          layoutIds: layoutIds,
        };
      });
  }, [blockoutForm, serviceListView]);

  const addLayoutNames = (table: ITableItem, filteredLayouts: ILayout[]) => {
    const layoutName = filteredLayouts
      .map((layout) => {
        if (layout.tables.some((layTab) => layTab === table._id)) {
          return layout.name;
        } else {
          return null;
        }
      })
      .filter(Boolean);
    table.layoutName = layoutName[0];
  };
  const handleSave = async () => {
    setSubmitting(true);

    if (timeOutCreateBlockOut) {
      clearTimeout(timeOutCreateBlockOut);
    }

    timeOutCreateBlockOut = setTimeout(() => {
      if (blockoutServiceType === BlockoutServiceType.BLOCKOUT_SERVICE) {
        const tasks = selectedTimeServices.map(async (timeService) => {
          const tableResult = await TablesLookupService.getTablesByLayoutIds(
            timeService.layoutIds,
            new Date(timeService.startDateTime), // can sometimes come through as a stringified Date, so here we make sure it's a Date object
            null
          );
          const filteredLayout = timeService.layoutIds.map((id) => LayoutsService.getLayoutBasedOnId(layouts, id));
          tableResult.tablesList.forEach((table) => addLayoutNames(table, filteredLayout));
          const tableListFromLayouts = TablesLookupService.getTablesInfoFromTableList(tableResult.tablesList);

          const blockouts: any = {
            blockedSections: [],
            blockedTables: tableListFromLayouts,
            ...timeService,
            repeatType: 'no-repeat',
            createdBy: createdBy || NBIProject.DIARY_MOBILE,
          };
          return BlockOutsService.save(blockouts);
        });

        Promise.all(tasks).then(async () => {
          // To reset Schedule Slide, so we can get latest data for blockout
          await dispatch(setSelectedSchedule({ useCachedScheduleLookup: false }));
          saveCallback && saveCallback('Blockout created');
          exit();
          setSubmitting(false);
        });
      } else if (blockoutServiceType === BlockoutServiceType.HIDE_SERVICE) {
        BlockOutsService.saveHiddenService({
          serviceIds: selectedServiceIds,
          date: DateUtilsService.dateToDateStr(selectedDate),
        }).then(async () => {
          saveCallback && saveCallback('Services hidden from booking widget');
          exit();
          setSubmitting(false);
        })
      }
    }, 200);
  };

  const handleChangeService = (type: string, value: boolean) => {
    if (blockoutServiceType === BlockoutServiceType.HIDE_SERVICE) {
      if (selectedServiceIds.includes(type)) {
        setSelectedServiceIds(selectedServiceIds.filter((id) => id !== type));
      } else {
        setSelectedServiceIds([...selectedServiceIds, type]);
      }
    } else {
      setBlockoutForm({
        ...blockoutForm,
        [type]: value,
      });
      setBlockAllServices(false);
    }
  };

  const selectedIds = Object.keys(blockoutForm).filter((id) => blockoutForm[id]);

  const ableToSave = blockoutServiceType === BlockoutServiceType.BLOCKOUT_SERVICE ? Boolean(selectedTimeServices.length) : selectedServiceIds.length > 0;

  const handleToggle = () => {
    if (blockoutServiceType === BlockoutServiceType.BLOCKOUT_SERVICE) {
      const updatedSelected = serviceListView.reduce((result, { service }) => {
        return {
          ...result,
          [service._id]: !blockAllServices,
        };
      }, {});
      setBlockoutForm({
        ...blockoutForm,
        ...updatedSelected,
      });
      setBlockAllServices(!blockAllServices);
    } else {
      if (hideAllServices) {
        setSelectedServiceIds([]);
      } else {
        setSelectedServiceIds(serviceListView.filter(({service}) => {
          return !originalHidingServiceIds.includes(service._id);
        }).map(({ service }) => service._id));
      }
      setHideAllServices(!hideAllServices);
    }
  };
  const blockoutServiceClickHandler = useCallback(() => {
    setBlockoutServiceType(BlockoutServiceType.BLOCKOUT_SERVICE);
  }, []);
  const hideServiceClickHandler = useCallback(() => {
    setBlockoutServiceType(BlockoutServiceType.HIDE_SERVICE);
  }, []);
  return (
    <div className={classNames({
      [style.containerBlockout]: !isWideVersion,
      [style.containerBlockoutWideVersion]: isWideVersion,
    })}>
      <div className={style.spaceOutside}>
        <div className={style.header}>
          <div className={style.btnWrap}>
            <ActionButton
              fullWidth={true}
              variant={'secondary'}
              size={'regular'}
              text={'Cancel'}
              handleClick={exit}
            />
          </div>
          <span className={style.title}>New Blockout</span>
          <div>
            <ActionButton
              fullWidth={true}
              variant={'primary'}
              size={'regular'}
              text={blockoutServiceType === BlockoutServiceType.BLOCKOUT_SERVICE ? 'Create' : 'Hide'}
              disabled={!ableToSave || submitting}
              handleClick={handleSave}
              data-testid={`save-blockoout`}
            />
          </div>
        </div>
      </div>
      <div className={style.containerNew}>
        <div className={style.typeButtonContainer}>
          <div
            className={classNames(style.typeButton, {
              [style.typeButtonActive]: blockoutServiceType === BlockoutServiceType.BLOCKOUT_SERVICE,
            })}
            onClick={blockoutServiceClickHandler}
          >Blockout service</div>
          <div
            className={classNames(style.typeButton, {
              [style.typeButtonActive]: blockoutServiceType === BlockoutServiceType.HIDE_SERVICE,
            })}
            onClick={hideServiceClickHandler}
          >Hide service</div>
        </div>
        {
          blockoutServiceType === BlockoutServiceType.BLOCKOUT_SERVICE && (
            <Box display="flex" style={{ gap: 12, marginTop: 12, marginBottom: 8, flexDirection: 'column' }}>
              <Typography color="textPrimary" style={{fontSize: 16}}>Select a service to blockout all tables within it</Typography>
              <Typography color="textPrimary" style={{fontSize: 14}}>
                Note: If a service overlaps with another, tables during the overlapping period won't be available for
                booking, regardless of their assigned service.
              </Typography>
            </Box>
          )
        }
        {
          blockoutServiceType === BlockoutServiceType.HIDE_SERVICE && (
            <Box display="flex" style={{ gap: 8, marginTop: 12, marginBottom: 8 }}>
              <Typography color="textPrimary" style={{fontSize: 16}}>
                Select a service to hide on the booking widget, making it unavailable to customers but still accessible to staff in the diary.
              </Typography>
            </Box>
          )
        }
        <Box
          onClick={handleToggle}
          alignItems="center"
          display="flex"
          style={{
            marginTop: 12,
            borderBottom: `1px solid ${Colors.get(APP_COLORS.clrGreyLight)}`,
          }}
        >
          <Checkbox
            checked={blockoutServiceType === BlockoutServiceType.BLOCKOUT_SERVICE ? !!blockAllServices : !!hideAllServices}
            onChange={handleToggle}
            color="primary"
            name={'all'}
            inputProps={{ 'aria-label': 'primary checkbox' }}
            data-testid={`select-blockout-service-all`}
          />
          <Typography
            style={{ marginRight: 12, marginLeft: 12, width: '30%' }}
            className={style.ellipsis}
            color={'textPrimary'}
          >
            {'all'}
          </Typography>
        </Box>
        {serviceListView.map(({ service: { _id, name, startTime, endTime } }, index) => {
          const handleOnCheck = () => handleChangeService(_id, !blockoutForm?.[_id]);
          const isLast = index === serviceListView.length - 1;
          const isExisted = blockoutServiceType === BlockoutServiceType.BLOCKOUT_SERVICE ?
            blockouts.find(
            ({ startDateTime, endDateTime }) =>
              startDateTime.toString() === startTime.toString() &&
              endDateTime.toString() === endTime.toString()
          ) : originalHidingServiceIds.includes(_id);
          const isCheck = blockoutServiceType === BlockoutServiceType.BLOCKOUT_SERVICE ? (isExisted ? false : !!blockoutForm?.[_id]) : selectedServiceIds.includes(_id) || originalHidingServiceIds.includes(_id);
          const isSelected = selectedIds.includes(_id);
          const tailText = blockoutServiceType === BlockoutServiceType.HIDE_SERVICE ?
            `${name} service already hidden for widget`
            : `Block out for ${name} service already exists`;
          return (
            <CheckListItem
              checked={isCheck}
              handleOnCheck={handleOnCheck}
              isLast={isLast}
              isExisted={Boolean(isExisted)}
              _id={_id}
              name={name}
              index={index}
              tailText={tailText}
              key={_id}
            />
          );
        })}
      </div>
    </div>
  );
}
const CheckListItem = ({
                         handleOnCheck, isLast, checked, _id, isExisted, index, name, tailText
                       }:{
  handleOnCheck: () => void,
  isLast: boolean,
  checked: boolean,
  _id: string,
  isExisted: boolean,
  index: number,
  name: string,
  tailText: string
}) => {
  return (
    <Box
      onClick={handleOnCheck}
      alignItems="center"
      display="flex"
      style={{
        marginTop: 12,
        borderBottom: isLast
          ? 'none'
          : `1px solid ${Colors.get(APP_COLORS.clrGreyLight)}`,
      }}
    >
      <Checkbox
        checked={checked}
        onChange={handleOnCheck}
        color="primary"
        name={_id}
        disabled={Boolean(isExisted)}
        inputProps={{ 'aria-label': 'primary checkbox' }}
        data-testid={`select-blockout-service-${index}`}
      />
      <Typography
        style={{ marginRight: 12, marginLeft: 12, width: '30%' }}
        className={style.ellipsis}
        color={isExisted ? 'textSecondary' : 'textPrimary'}
      >
        {name}
      </Typography>
      {isExisted ? (
        <Typography
          style={{
            color: Colors.get(APP_COLORS.clrWarningLight),
            textAlign: 'left',
            width: '65%',
          }}
        >
          {tailText}
        </Typography>
      ) : null}
    </Box>
  )
}
